import { ref, computed } from 'vue'
import presupuestosServicio from '@/services/finanzas/presupuestosServicio'
import categoriasServicio from '@/services/finanzas/categoriasServicio'
import Swal from 'sweetalert2'

/**
 * Composable para presupuestos
 */
export function usePresupuestos() {
  const presupuestos = ref([])
  const categorias = ref([])
  const comparativa = ref(null)
  const cargando = ref(false)
  const error = ref(null)

  const filtros = ref({
    periodo: null,
    anio: new Date().getFullYear(),
    categoria_id: null
  })

  /**
   * Cargar presupuestos
   */
  const cargarPresupuestos = async (params = {}) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await presupuestosServicio.listar({ ...filtros.value, ...params })
      presupuestos.value = respuesta.data || respuesta
      return presupuestos.value
    } catch (err) {
      error.value = err.mensaje || 'Error al cargar presupuestos'
      console.error(error.value, err)
      Swal.fire('Error', error.value, 'error')
      return []
    } finally {
      cargando.value = false
    }
  }

  /**
   * Cargar categorías (solo de tipo gasto)
   */
  const cargarCategorias = async () => {
    try {
      const respuesta = await categoriasServicio.listar({ tipo: 'gasto', solo_activas: true })
      categorias.value = respuesta.data || respuesta
      return categorias.value
    } catch (err) {
      console.error('Error al cargar categorías:', err)
      return []
    }
  }

  /**
   * Crear presupuesto
   */
  const crearPresupuesto = async (datos) => {
    cargando.value = true
    error.value = null
    try {
      await presupuestosServicio.crear(datos)
      await Swal.fire('Éxito', 'Presupuesto creado correctamente', 'success')
      await cargarPresupuestos()
      return true
    } catch (err) {
      error.value = err.mensaje || 'Error al crear presupuesto'
      Swal.fire('Error', error.value, 'error')
      return false
    } finally {
      cargando.value = false
    }
  }

  /**
   * Actualizar presupuesto
   */
  const actualizarPresupuesto = async (id, datos) => {
    cargando.value = true
    error.value = null
    try {
      await presupuestosServicio.actualizar(id, datos)
      await Swal.fire('Éxito', 'Presupuesto actualizado correctamente', 'success')
      await cargarPresupuestos()
      return true
    } catch (err) {
      error.value = err.mensaje || 'Error al actualizar presupuesto'
      Swal.fire('Error', error.value, 'error')
      return false
    } finally {
      cargando.value = false
    }
  }

  /**
   * Eliminar presupuesto
   */
  const eliminarPresupuesto = async (id) => {
    const confirmacion = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    })

    if (!confirmacion.isConfirmed) return false

    cargando.value = true
    error.value = null
    try {
      await presupuestosServicio.eliminar(id)
      await Swal.fire('Eliminado', 'Presupuesto eliminado correctamente', 'success')
      await cargarPresupuestos()
      return true
    } catch (err) {
      error.value = err.mensaje || 'Error al eliminar presupuesto'
      Swal.fire('Error', error.value, 'error')
      return false
    } finally {
      cargando.value = false
    }
  }

  /**
   * Obtener comparativa de presupuesto vs real
   */
  const obtenerComparativa = async (params = {}) => {
    cargando.value = true
    error.value = null
    try {
      const respuesta = await presupuestosServicio.obtenerComparativa({
        anio: filtros.value.anio,
        ...params
      })
      comparativa.value = respuesta
      return respuesta
    } catch (err) {
      error.value = err.mensaje || 'Error al obtener comparativa'
      console.error(error.value, err)
      Swal.fire('Error', error.value, 'error')
      return null
    } finally {
      cargando.value = false
    }
  }

  /**
   * Presupuestos por periodo
   */
  const presupuestosPorPeriodo = computed(() => {
    if (!filtros.value.periodo) return presupuestos.value

    return presupuestos.value.filter(p => p.periodo === filtros.value.periodo)
  })

  /**
   * Total presupuestado
   */
  const totalPresupuestado = computed(() => {
    return presupuestos.value.reduce((sum, p) => sum + Number(p.monto_estimado || 0), 0)
  })

  return {
    presupuestos,
    categorias,
    comparativa,
    cargando,
    error,
    filtros,
    presupuestosPorPeriodo,
    totalPresupuestado,
    cargarPresupuestos,
    cargarCategorias,
    crearPresupuesto,
    actualizarPresupuesto,
    eliminarPresupuesto,
    obtenerComparativa
  }
}

export default usePresupuestos
